/**
 * Network Aware Scheduler
 * 
 * Handles network-dependent scheduling logic, quality-based decisions,
 * and adaptive scheduling based on connection type and data usage patterns.
 */

import { BackgroundTaskScheduler } from './BackgroundTaskScheduler';
import { SyncScheduleConfig } from './BaseScheduler';

/**
 * Network connection types
 */
export type NetworkConnectionType = 'wifi' | 'cellular' | 'ethernet' | 'unknown' | 'none';

/**
 * Network quality levels
 */
export type NetworkQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'offline';

/**
 * Network information
 */
export interface NetworkInfo {
  connectionType: NetworkConnectionType;
  quality: NetworkQuality;
  isConnected: boolean;
  isMetered: boolean;
  estimatedBandwidth?: number; // Mbps
}

/**
 * Network Aware Scheduler
 * 
 * Extends BackgroundTaskScheduler to add network-aware scheduling capabilities,
 * including connection type optimization, data usage management, and quality-based decisions.
 */
export class NetworkAwareScheduler extends BackgroundTaskScheduler {
  private networkInfo: NetworkInfo = {
    connectionType: 'unknown',
    quality: 'offline',
    isConnected: false,
    isMetered: false
  };

  constructor(config: Partial<SyncScheduleConfig> = {}) {
    super(config);
  }

  /**
   * Update network information
   */
  updateNetworkInfo(networkInfo: Partial<NetworkInfo>): void {
    const previousInfo = { ...this.networkInfo };
    this.networkInfo = { ...this.networkInfo, ...networkInfo };

    // Handle network state changes
    this.handleNetworkChange(previousInfo, this.networkInfo);
  }

  /**
   * Handle network state changes
   */
  private handleNetworkChange(previous: NetworkInfo, current: NetworkInfo): void {
    // Connection restored
    if (!previous.isConnected && current.isConnected) {
      console.log('📶 Network connection restored');
      this.scheduleHighPrioritySync('network_restored');
      return;
    }

    // Connection lost
    if (previous.isConnected && !current.isConnected) {
      console.log('📶 Network connection lost');
      this.emitEvent({
        type: 'data_optimization_activated',
        data: {
          connectionType: 'none',
          reason: 'Connection lost'
        }
      });
      return;
    }

    // Connection type changed
    if (current.isConnected && previous.connectionType !== current.connectionType) {
      console.log(`📶 Network type changed: ${previous.connectionType} → ${current.connectionType}`);
      this.handleConnectionTypeChange(current.connectionType);
    }

    // Quality changed
    if (current.isConnected && previous.quality !== current.quality) {
      console.log(`📶 Network quality changed: ${previous.quality} → ${current.quality}`);
      this.handleQualityChange(current.quality);
    }
  }

  /**
   * Handle connection type changes
   */
  private handleConnectionTypeChange(connectionType: NetworkConnectionType): void {
    switch (connectionType) {
      case 'wifi':
      case 'ethernet':
        // High-speed connection: schedule immediate sync
        this.scheduleHighPrioritySync('high_speed_connection');
        this.emitEvent({
          type: 'data_optimization_activated',
          data: {
            connectionType,
            reason: 'High-speed connection available'
          }
        });
        break;

      case 'cellular':
        // Cellular connection: respect data usage settings
        if (this.config.respectDataUsage) {
          this.emitEvent({
            type: 'data_optimization_activated',
            data: {
              connectionType,
              reason: 'Cellular connection - data optimization active'
            }
          });
        }
        break;

      default:
        console.log(`📶 Unknown connection type: ${connectionType}`);
    }
  }

  /**
   * Handle network quality changes
   */
  private handleQualityChange(quality: NetworkQuality): void {
    switch (quality) {
      case 'excellent':
        this.scheduleHighPrioritySync('quality_improved');
        break;

      case 'poor':
        // Poor quality: increase sync interval
        this.scheduleNextSync('low', 'poor_network_quality');
        break;

      case 'offline':
        console.log('📶 Network went offline');
        break;
    }
  }

  /**
   * Check if sync should be postponed based on network conditions
   */
  private shouldPostponeSyncNetwork(): string | null {
    // Check network connectivity
    if (!this.networkInfo.isConnected) {
      return 'No network connection';
    }

    // Check network quality
    if (this.networkInfo.quality === 'poor') {
      return 'Poor network quality';
    }

    // Check data usage restrictions on cellular
    if (this.config.respectDataUsage &&
        this.networkInfo.connectionType === 'cellular' &&
        this.networkInfo.isMetered) {
      return 'Cellular data usage restrictions';
    }

    return null;
  }

  /**
   * Override the parent method to include network checks
   */
  protected shouldPostponeSync(): string | null {
    // Call parent method first
    const parentReason = super.shouldPostponeSync();
    if (parentReason) {
      return parentReason;
    }

    // Check network-specific conditions
    return this.shouldPostponeSyncNetwork();
  }

  /**
   * Calculate optimal interval with network considerations
   */
  protected calculateOptimalInterval(priority: 'high' | 'normal' | 'low'): number {
    let interval = super.calculateOptimalInterval(priority);

    // Adjust based on network conditions
    if (this.networkInfo.isConnected) {
      switch (this.networkInfo.quality) {
        case 'excellent':
          // Excellent connection: reduce interval for more frequent syncs
          interval = Math.max(this.config.minInterval, interval * 0.7);
          break;

        case 'good':
          // Good connection: slight reduction
          interval = Math.max(this.config.minInterval, interval * 0.85);
          break;

        case 'fair':
          // Fair connection: use default interval
          break;

        case 'poor':
          // Poor connection: increase interval
          interval = Math.min(this.config.maxInterval, interval * 1.5);
          break;
      }

      // Adjust for cellular connections
      if (this.config.respectDataUsage && 
          this.networkInfo.connectionType === 'cellular') {
        interval = Math.min(this.config.maxInterval, interval * 1.3);
      }
    } else {
      // No connection: use maximum interval
      interval = this.config.maxInterval;
    }

    return interval;
  }

  /**
   * Check if large sync should be performed based on connection
   */
  shouldPerformLargeSync(estimatedSize: number): boolean {
    if (!this.networkInfo.isConnected) {
      return false;
    }

    // Always allow on WiFi/Ethernet
    if (this.networkInfo.connectionType === 'wifi' || 
        this.networkInfo.connectionType === 'ethernet') {
      return true;
    }

    // Check cellular restrictions
    if (this.config.respectDataUsage && 
        this.networkInfo.connectionType === 'cellular') {
      
      if (!this.config.preferWifiForLargeSync) {
        return estimatedSize <= this.config.maxCellularSyncSize;
      }
      
      // Prefer WiFi for large syncs
      return false;
    }

    // Unknown connection: be conservative
    return estimatedSize <= this.config.maxCellularSyncSize;
  }

  /**
   * Get network-optimized sync strategy
   */
  getOptimizedSyncStrategy(): {
    shouldSync: boolean;
    reason: string;
    recommendedInterval?: number;
  } {
    if (!this.networkInfo.isConnected) {
      return {
        shouldSync: false,
        reason: 'No network connection'
      };
    }

    const quality = this.networkInfo.quality;
    const connectionType = this.networkInfo.connectionType;

    // Excellent conditions
    if (quality === 'excellent' && 
        (connectionType === 'wifi' || connectionType === 'ethernet')) {
      return {
        shouldSync: true,
        reason: 'Optimal network conditions',
        recommendedInterval: this.config.minInterval
      };
    }

    // Good conditions
    if (quality === 'good' || quality === 'excellent') {
      return {
        shouldSync: true,
        reason: 'Good network conditions',
        recommendedInterval: this.config.defaultInterval
      };
    }

    // Fair conditions
    if (quality === 'fair') {
      return {
        shouldSync: true,
        reason: 'Acceptable network conditions',
        recommendedInterval: this.config.defaultInterval * 1.2
      };
    }

    // Poor conditions
    return {
      shouldSync: false,
      reason: 'Poor network quality - postponing sync'
    };
  }

  /**
   * Get current network information
   */
  getNetworkInfo(): NetworkInfo {
    return { ...this.networkInfo };
  }

  /**
   * Estimate sync data size based on pending operations
   */
  async estimateSyncDataSize(): Promise<number> {
    try {
      // This would typically check pending operations in the sync queue
      // For now, return a conservative estimate
      const pendingSyncs = this.getScheduledSyncs();
      const baseSizePerSync = 50 * 1024; // 50KB base estimate
      
      return pendingSyncs.size * baseSizePerSync;
    } catch (error) {
      console.warn('Failed to estimate sync data size:', error);
      return this.config.maxCellularSyncSize; // Conservative fallback
    }
  }

  /**
   * Schedule network-optimized sync
   */
  async scheduleNetworkOptimizedSync(reason: string): Promise<void> {
    const strategy = this.getOptimizedSyncStrategy();
    
    if (!strategy.shouldSync) {
      console.log(`📶 Sync postponed: ${strategy.reason}`);
      return;
    }

    const estimatedSize = await this.estimateSyncDataSize();
    
    if (!this.shouldPerformLargeSync(estimatedSize)) {
      console.log(`📶 Large sync postponed: ${estimatedSize} bytes on ${this.networkInfo.connectionType}`);
      return;
    }

    // Determine priority based on network conditions
    let priority: 'high' | 'normal' | 'low' = 'normal';
    
    if (this.networkInfo.quality === 'excellent' && 
        (this.networkInfo.connectionType === 'wifi' || this.networkInfo.connectionType === 'ethernet')) {
      priority = 'high';
    } else if (this.networkInfo.quality === 'poor') {
      priority = 'low';
    }

    this.scheduleNextSync(priority, `${reason} (${strategy.reason})`);
  }
}
